import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  ChevronRight,
  FileText,
  ImagePlus,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import type { BannerPlacement, CmsPage as CmsPageType } from '@terra/api-client';
import { Input, Spinner } from '@terra/ui';

import { MediaLibraryPicker } from '../components/MediaLibraryPicker';
import { api } from '../lib/api';

// ═══════════════════════════════════════════════════════════════════════════════
// SHARED — Image uploader for banner/slider sections
// ═══════════════════════════════════════════════════════════════════════════════

interface BannerRow {
  id?: string;
  imageUrl: string;
  linkUrl: string;
  // Optional viewport-specific images. Storefront <ResponsiveBanner /> falls
  // back to `imageUrl` if a slot is empty — admin can leave them blank.
  // Explicit `| undefined` allows `onPatch({ tabletImageUrl: undefined })` style
  // clears under `exactOptionalPropertyTypes`.
  tabletImageUrl?: string | undefined;
  mobileImageUrl?: string | undefined;
  // Optional scheduling — banner only shows on storefront inside this window.
  // ISO datetime strings (`<input type="datetime-local">` value compatible).
  startsAt?: string | undefined;
  endsAt?: string | undefined;
  // local UI state for pending uploads (not persisted)
  file?: File | undefined;
  previewUrl?: string | undefined;
  tabletFile?: File | undefined;
  tabletPreviewUrl?: string | undefined;
  mobileFile?: File | undefined;
  mobilePreviewUrl?: string | undefined;
}

/** Single visual slot — used by desktop / tablet / mobile rows of BannerUploadRow. */
function ImageSlot({
  label,
  imageUrl,
  previewUrl,
  file,
  onChange,
  onClear,
  onOpenLibrary,
}: {
  label: string;
  imageUrl: string | undefined;
  previewUrl: string | undefined;
  file: File | undefined;
  onChange: (file: File, previewUrl: string) => void;
  onClear: () => void;
  /** When provided, shows a "Library" button that opens the MediaLibraryPicker. */
  onOpenLibrary?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const shown = previewUrl || imageUrl;
  return (
    <div className="flex shrink-0 flex-col gap-1.5">
      <span className="text-ink-muted text-[10px] font-bold uppercase tracking-wider">{label}</span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12px] font-bold transition-colors"
          title="Upload from your device"
        >
          Upload
        </button>
        {onOpenLibrary && (
          <button
            type="button"
            onClick={onOpenLibrary}
            className="bg-surface-subtle border-border/60 text-ink hover:bg-surface flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-bold transition-colors"
            title="Pick from the media library"
          >
            Library
          </button>
        )}
        {shown && (
          <button
            type="button"
            onClick={onClear}
            className="grid h-8 w-8 place-items-center rounded-lg text-red-500 transition-colors hover:bg-red-50"
            aria-label={`Remove ${label}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onChange(f, URL.createObjectURL(f));
        }}
      />
      {shown && (
        <img src={shown} alt="" className="h-14 w-24 rounded border object-cover shadow-sm" />
      )}
      {file && <p className="text-ink-subtle text-[10px]">{(file.size / 1024).toFixed(0)} KB</p>}
    </div>
  );
}

function BannerUploadRow({
  row,
  onPatch,
  onRemove,
}: {
  row: BannerRow;
  onPatch: (patch: Partial<BannerRow>) => void;
  onRemove: () => void;
}) {
  // Tracks which slot is currently sourcing from the media library. `null`
  // means the library modal is closed. When a file is selected, we patch the
  // corresponding *ImageUrl field directly (skipping the upload pipeline since
  // the chosen file already has a stable URL in our FileObject store).
  const [pickerSlot, setPickerSlot] = useState<'desktop' | 'tablet' | 'mobile' | null>(null);
  return (
    <div className="border-border bg-surface space-y-3 rounded-xl border p-4 transition-all duration-200 hover:shadow-sm">
      <MediaLibraryPicker
        open={pickerSlot !== null}
        onClose={() => setPickerSlot(null)}
        onSelect={(file) => {
          if (!file.url) return;
          if (pickerSlot === 'desktop') {
            onPatch({ imageUrl: file.url, file: undefined, previewUrl: undefined });
          } else if (pickerSlot === 'tablet') {
            onPatch({
              tabletImageUrl: file.url,
              tabletFile: undefined,
              tabletPreviewUrl: undefined,
            });
          } else if (pickerSlot === 'mobile') {
            onPatch({
              mobileImageUrl: file.url,
              mobileFile: undefined,
              mobilePreviewUrl: undefined,
            });
          }
        }}
        title={
          pickerSlot === 'desktop'
            ? 'Select desktop image'
            : pickerSlot === 'tablet'
              ? 'Select tablet image'
              : 'Select mobile image'
        }
      />
      {/* Top row: 3 image slots (desktop required, tablet + mobile optional) */}
      <div className="flex flex-wrap items-start gap-4">
        <ImageSlot
          label="Desktop *"
          imageUrl={row.imageUrl}
          previewUrl={row.previewUrl}
          file={row.file}
          onChange={(file, previewUrl) => onPatch({ file, previewUrl })}
          onClear={() => onPatch({ file: undefined, previewUrl: undefined, imageUrl: '' })}
          onOpenLibrary={() => setPickerSlot('desktop')}
        />
        <ImageSlot
          label="Tablet (opt.)"
          imageUrl={row.tabletImageUrl}
          previewUrl={row.tabletPreviewUrl}
          file={row.tabletFile}
          onChange={(tabletFile, tabletPreviewUrl) => onPatch({ tabletFile, tabletPreviewUrl })}
          onClear={() =>
            onPatch({ tabletFile: undefined, tabletPreviewUrl: undefined, tabletImageUrl: '' })
          }
          onOpenLibrary={() => setPickerSlot('tablet')}
        />
        <ImageSlot
          label="Mobile (opt.)"
          imageUrl={row.mobileImageUrl}
          previewUrl={row.mobilePreviewUrl}
          file={row.mobileFile}
          onChange={(mobileFile, mobilePreviewUrl) => onPatch({ mobileFile, mobilePreviewUrl })}
          onClear={() =>
            onPatch({ mobileFile: undefined, mobilePreviewUrl: undefined, mobileImageUrl: '' })
          }
          onOpenLibrary={() => setPickerSlot('mobile')}
        />
        <button
          type="button"
          onClick={onRemove}
          className="ml-auto grid h-9 w-9 shrink-0 place-items-center self-start rounded-lg text-red-500 transition-colors hover:bg-red-50"
          aria-label="Remove banner"
          title="Remove this banner"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Link URL */}
      <div className="min-w-0 flex-1">
        <span className="text-ink-muted mb-1 block text-[10px] font-bold uppercase tracking-wider">
          Link URL
        </span>
        <Input
          placeholder="https://… or /category/spices"
          value={row.linkUrl}
          onChange={(e) => onPatch({ linkUrl: e.target.value })}
        />
      </div>

      {/* Schedule window (optional) */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <span className="text-ink-muted mb-1 block text-[10px] font-bold uppercase tracking-wider">
            Starts At (optional)
          </span>
          <input
            type="datetime-local"
            value={row.startsAt ?? ''}
            onChange={(e) => onPatch({ startsAt: e.target.value })}
            className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
          />
        </div>
        <div>
          <span className="text-ink-muted mb-1 block text-[10px] font-bold uppercase tracking-wider">
            Ends At (optional)
          </span>
          <input
            type="datetime-local"
            value={row.endsAt ?? ''}
            onChange={(e) => onPatch({ endsAt: e.target.value })}
            className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
          />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 2 — Homepage Settings (Tabbed)
// ═══════════════════════════════════════════════════════════════════════════════

const HOMEPAGE_TABS: { key: BannerPlacement; label: string }[] = [
  { key: 'HOME_SLIDER', label: 'Home Slider' },
  { key: 'FLASH_DEALS', label: 'Flash deals' },
  { key: 'TODAYS_DEAL', label: 'Todays Deal' },
  { key: 'BANNER_LEVEL_1', label: 'Banner Level 1' },
  { key: 'BANNER_LEVEL_2', label: 'Banner Level 2' },
  { key: 'BANNER_LEVEL_3', label: 'Banner Level 3' },
  { key: 'CATEGORY_PRODUCTS', label: 'Category Wise Products' },
  { key: 'CLASSIFIEDS', label: 'Classifieds' },
  { key: 'TOP_BRANDS', label: 'Top Brands' },
  { key: 'SPONSORED_SHOWCASE', label: 'Sponsored Showcase' },
];

export function HomepageSettingsPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<BannerPlacement>('HOME_SLIDER');
  const [rows, setRows] = useState<BannerRow[]>([]);
  const [saving, setSaving] = useState(false);

  const { data: banners, isLoading } = useQuery({
    queryKey: ['cms-banners-admin', activeTab],
    queryFn: () => api.cms.listBanners(activeTab),
  });

  // sync rows from server data when tab changes
  const prevTabRef = useRef(activeTab);
  if (prevTabRef.current !== activeTab) {
    prevTabRef.current = activeTab;
    setRows([]);
  }

  // ISO date string ('2026-05-25T18:00:00.000Z') → 'YYYY-MM-DDTHH:mm' for
  // <input type="datetime-local"> value. Returns undefined for null/empty.
  function isoToLocalInput(iso: string | null | undefined): string | undefined {
    if (!iso) return undefined;
    return iso.slice(0, 16);
  }

  const displayRows: BannerRow[] =
    rows.length > 0
      ? rows
      : (banners ?? []).map((b) => {
          const r: BannerRow = {
            id: b.id,
            imageUrl: b.imageUrl,
            linkUrl: b.linkUrl ?? '',
          };
          if (b.tabletImageUrl) r.tabletImageUrl = b.tabletImageUrl;
          if (b.mobileImageUrl) r.mobileImageUrl = b.mobileImageUrl;
          const s = isoToLocalInput(b.startsAt);
          if (s) r.startsAt = s;
          const e = isoToLocalInput(b.endsAt);
          if (e) r.endsAt = e;
          return r;
        });

  function addRow() {
    setRows([...displayRows, { imageUrl: '', linkUrl: '' }]);
  }

  function updateRow(idx: number, patch: Partial<BannerRow>) {
    const next = [...displayRows];
    next[idx] = { ...next[idx]!, ...patch };
    setRows(next);
  }

  function removeRow(idx: number) {
    setRows(displayRows.filter((_, i) => i !== idx));
  }

  // Upload one File to Cloudinary and return the secure_url (or undefined if
  // the upload failed). Re-used for desktop / tablet / mobile slots.
  async function uploadFile(file: File): Promise<string | undefined> {
    try {
      const sig = await api.files.getUploadSignature({ folder: 'banners' });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('api_key', sig.apiKey);
      fd.append('timestamp', String(sig.timestamp));
      fd.append('signature', sig.signature);
      fd.append('folder', sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
        method: 'POST',
        body: fd,
      });
      const out = (await res.json()) as { secure_url?: string };
      return out.secure_url;
    } catch {
      return undefined;
    }
  }

  // Convert datetime-local input value ('2026-05-25T18:00') to ISO string.
  // Returns null when the input is empty (caller sends null to clear schedule).
  function localInputToIso(v: string | undefined): string | null {
    if (!v) return null;
    const d = new Date(v);
    return Number.isFinite(d.getTime()) ? d.toISOString() : null;
  }

  async function handleSave() {
    setSaving(true);
    try {
      // delete existing banners for this placement
      const existing = banners ?? [];
      for (const b of existing) {
        await api.cms.deleteBanner(b.id);
      }

      // create new banners
      for (let i = 0; i < displayRows.length; i++) {
        const row = displayRows[i]!;
        let imageUrl = row.imageUrl;
        let tabletImageUrl = row.tabletImageUrl ?? '';
        let mobileImageUrl = row.mobileImageUrl ?? '';

        // Upload any pending files (desktop / tablet / mobile) in parallel.
        // If an upload fails the slot stays at its current URL (or empty).
        const [d, t, m] = await Promise.all([
          row.file ? uploadFile(row.file) : Promise.resolve(undefined),
          row.tabletFile ? uploadFile(row.tabletFile) : Promise.resolve(undefined),
          row.mobileFile ? uploadFile(row.mobileFile) : Promise.resolve(undefined),
        ]);
        if (d) imageUrl = d;
        if (t) tabletImageUrl = t;
        if (m) mobileImageUrl = m;

        if (imageUrl) {
          await api.cms.createBanner({
            placement: activeTab,
            imageUrl,
            tabletImageUrl: tabletImageUrl || null,
            mobileImageUrl: mobileImageUrl || null,
            linkUrl: row.linkUrl || null,
            position: i,
            startsAt: localInputToIso(row.startsAt),
            endsAt: localInputToIso(row.endsAt),
          });
        }
      }

      void qc.invalidateQueries({ queryKey: ['cms-banners-admin', activeTab] });
      setRows([]);
    } finally {
      setSaving(false);
    }
  }

  const tabInfo = HOMEPAGE_TABS.find((t) => t.key === activeTab)!;

  return (
    <div className="space-y-6 pb-8">
      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        {/* Tab sidebar */}
        <div className="border-border/60 bg-surface h-fit self-start overflow-hidden rounded-2xl border shadow-sm">
          <nav className="flex flex-col p-2">
            {HOMEPAGE_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex w-full items-center justify-between rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-brand-primary text-white shadow-sm'
                    : 'text-ink-muted hover:bg-surface-subtle hover:text-ink'
                }`}
              >
                <span
                  className={`text-[13px] font-bold ${activeTab === tab.key ? 'text-white' : 'text-ink'}`}
                >
                  {tab.label}
                </span>
                <ChevronRight
                  className={`h-4 w-4 shrink-0 transition-transform ${activeTab === tab.key ? 'rotate-90 text-white' : 'text-ink-muted/50'}`}
                />
              </button>
            ))}
          </nav>
        </div>

        {/* Content area */}
        <div className="space-y-4">
          <div className="border-border/60 bg-surface flex items-center justify-between rounded-2xl border px-5 py-4 shadow-sm">
            <h3 className="text-ink text-[14px] font-bold">{tabInfo.label} Configuration</h3>
            <div className="bg-surface-subtle/50 border-border/40 flex items-center gap-2 rounded-lg border px-3 py-1.5">
              <span className="text-lg">🇬🇧</span>
              <span className="text-ink text-[12px] font-bold">English</span>
            </div>
          </div>

          {activeTab === 'HOME_SLIDER' && (
            <div className="border-brand-primary/20 bg-brand-primary/5 flex gap-3 rounded-2xl border p-4">
              <AlertCircle className="text-brand-primary h-5 w-5 shrink-0" />
              <div>
                <h4 className="text-brand-primary mb-1 text-[13px] font-bold">Upload Guidelines</h4>
                <p className="text-ink-muted text-[12px] font-medium leading-relaxed">
                  Minimum dimensions required:{' '}
                  <strong className="text-ink">1903px width X 553px height</strong>.
                  <br />
                  We have limited banner height to maintain UI. Banners may be cropped from left &
                  right sides on smaller devices to maintain responsiveness. Keep this in mind when
                  designing.
                </p>
              </div>
            </div>
          )}

          <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
            <div className="space-y-4 p-5">
              {isLoading ? (
                <div className="grid min-h-[200px] place-items-center">
                  <Spinner size="lg" />
                </div>
              ) : (
                <div className="space-y-3">
                  {displayRows.map((row, idx) => (
                    <BannerUploadRow
                      key={row.id ?? `new-${idx}`}
                      row={row}
                      onPatch={(patch) => updateRow(idx, patch)}
                      onRemove={() => removeRow(idx)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={addRow}
                    className="border-border/60 bg-surface-subtle/20 text-ink-muted hover:border-brand-primary/50 hover:bg-brand-primary/5 hover:text-brand-primary flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed py-4 text-[13px] font-bold transition-all duration-200"
                  >
                    <Plus className="h-4 w-4" /> Add Banner
                  </button>
                </div>
              )}
            </div>

            <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
              <button
                disabled={saving}
                onClick={() => void handleSave()}
                className="bg-ink hover:bg-ink/90 flex h-9 min-w-[120px] items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
              >
                {saving ? (
                  <Spinner size="sm" className="text-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}{' '}
                Save Banners
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 3 — Authentication Layout & Settings
// ═══════════════════════════════════════════════════════════════════════════════

const AUTH_LAYOUTS = [
  {
    id: 'modern',
    name: 'Authentication Layout',
    description: 'Sleek modern layout with side image panel.',
    gradient: 'from-slate-600 via-slate-700 to-slate-900',
  },
  {
    id: 'classic',
    name: 'Classic Auth',
    description: 'Centered form with background image.',
    gradient: 'from-violet-500 via-purple-600 to-indigo-700',
  },
];

export function AuthenticationLayoutPage() {
  const qc = useQueryClient();
  const adminInputRef = useRef<HTMLInputElement>(null);
  const customerInputRef = useRef<HTMLInputElement>(null);
  const [adminPreview, setAdminPreview] = useState<string | null>(null);
  const [customerPreview, setCustomerPreview] = useState<string | null>(null);
  const [adminFile, setAdminFile] = useState<File | null>(null);
  const [customerFile, setCustomerFile] = useState<File | null>(null);

  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'auth_layout'],
    queryFn: () => api.cms.getSetting('auth_layout'),
  });

  const current = (setting?.value as any)?.layoutId ?? 'modern';
  const adminImageUrl = (setting?.value as any)?.adminLoginImage ?? '';
  const customerImageUrl = (setting?.value as any)?.customerLoginImage ?? '';

  const upsert = useMutation({
    mutationFn: async (payload: {
      layoutId?: string;
      adminLoginImage?: string;
      customerLoginImage?: string;
    }) => {
      const prev = (setting?.value as any) ?? {};
      return api.cms.upsertSetting('auth_layout', { ...prev, ...payload });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['website-setting', 'auth_layout'] }),
  });

  async function handleSave() {
    const updates: any = { layoutId: current };

    // Upload admin image if selected
    if (adminFile) {
      try {
        const sig = await api.files.getUploadSignature({ folder: 'auth' });
        const fd = new FormData();
        fd.append('file', adminFile);
        fd.append('api_key', sig.apiKey);
        fd.append('timestamp', String(sig.timestamp));
        fd.append('signature', sig.signature);
        fd.append('folder', sig.folder);
        const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
          method: 'POST',
          body: fd,
        });
        const out = (await res.json()) as { secure_url?: string };
        if (out.secure_url) updates.adminLoginImage = out.secure_url;
      } catch {
        /* ignore */
      }
    }

    // Upload customer image if selected
    if (customerFile) {
      try {
        const sig = await api.files.getUploadSignature({ folder: 'auth' });
        const fd = new FormData();
        fd.append('file', customerFile);
        fd.append('api_key', sig.apiKey);
        fd.append('timestamp', String(sig.timestamp));
        fd.append('signature', sig.signature);
        fd.append('folder', sig.folder);
        const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
          method: 'POST',
          body: fd,
        });
        const out = (await res.json()) as { secure_url?: string };
        if (out.secure_url) updates.customerLoginImage = out.secure_url;
      } catch {
        /* ignore */
      }
    }

    upsert.mutate(updates);
  }

  return (
    <div className="space-y-6 pb-8">
      {isLoading ? (
        <div className="grid min-h-[300px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <div className="border-border/60 bg-surface rounded-2xl border p-6 shadow-sm">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {AUTH_LAYOUTS.map((layout) => (
                <div
                  key={layout.id}
                  className={`group relative cursor-pointer overflow-hidden rounded-2xl border-2 transition-all duration-300 ${
                    current === layout.id
                      ? 'border-brand-primary shadow-brand-primary/20 shadow-lg'
                      : 'border-border/60 hover:border-brand-primary/50 hover:shadow-md'
                  }`}
                  onClick={() => upsert.mutate({ layoutId: layout.id })}
                >
                  <div
                    className={`bg-gradient-to-br ${layout.gradient} flex h-40 items-center justify-center`}
                  >
                    <FileText className="h-14 w-14 text-white/90" />
                    {current === layout.id && (
                      <span className="bg-brand-primary absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full text-white shadow-md">
                        <Check className="h-5 w-5" strokeWidth={3} />
                      </span>
                    )}
                  </div>
                  <div className="bg-surface flex items-center justify-between p-5">
                    <div className="pr-4">
                      <p className="text-ink text-[14px] font-bold">{layout.name}</p>
                      <p className="text-ink-muted mt-1 line-clamp-1 text-[12px] font-medium leading-snug">
                        {layout.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
            <div className="border-border/60 bg-surface-subtle/30 border-b px-5 py-4">
              <h3 className="text-ink text-[14px] font-bold">Authentication Page Images</h3>
            </div>
            <div className="grid gap-8 p-5 md:grid-cols-2">
              <div>
                <h4 className="text-ink mb-3 text-[13px] font-bold">Admin Login Image</h4>
                <div className="mb-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => adminInputRef.current?.click()}
                    className="bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 flex h-9 items-center gap-2 rounded-xl px-4 text-[13px] font-bold transition-all"
                  >
                    Browse
                  </button>
                  <span className="text-ink-muted text-[12px] font-medium">
                    {adminFile
                      ? '1 File selected'
                      : adminImageUrl
                        ? '1 File selected'
                        : 'No file chosen'}
                  </span>
                  {(adminFile || adminImageUrl) && (
                    <button
                      type="button"
                      onClick={() => {
                        setAdminFile(null);
                        setAdminPreview(null);
                      }}
                      className="grid h-8 w-8 place-items-center rounded-lg text-red-500 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <input
                  ref={adminInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setAdminFile(f);
                      setAdminPreview(URL.createObjectURL(f));
                    }
                  }}
                />
                {adminPreview || adminImageUrl ? (
                  <img
                    src={adminPreview || adminImageUrl}
                    alt="Admin"
                    className="border-border/60 h-32 w-auto rounded-xl border object-cover shadow-sm"
                  />
                ) : (
                  <div className="border-border/60 bg-surface-subtle/30 grid h-32 w-full place-items-center rounded-xl border border-dashed">
                    <span className="text-ink-muted text-[12px] font-medium">
                      No image uploaded
                    </span>
                  </div>
                )}
              </div>

              <div>
                <h4 className="text-ink mb-3 text-[13px] font-bold">Customer Login Image</h4>
                <div className="mb-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => customerInputRef.current?.click()}
                    className="bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 flex h-9 items-center gap-2 rounded-xl px-4 text-[13px] font-bold transition-all"
                  >
                    Browse
                  </button>
                  <span className="text-ink-muted text-[12px] font-medium">
                    {customerFile
                      ? '1 File selected'
                      : customerImageUrl
                        ? '1 File selected'
                        : 'No file chosen'}
                  </span>
                  {(customerFile || customerImageUrl) && (
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerFile(null);
                        setCustomerPreview(null);
                      }}
                      className="grid h-8 w-8 place-items-center rounded-lg text-red-500 hover:bg-red-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <input
                  ref={customerInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setCustomerFile(f);
                      setCustomerPreview(URL.createObjectURL(f));
                    }
                  }}
                />
                {customerPreview || customerImageUrl ? (
                  <img
                    src={customerPreview || customerImageUrl}
                    alt="Customer"
                    className="border-border/60 h-32 w-auto rounded-xl border object-cover shadow-sm"
                  />
                ) : (
                  <div className="border-border/60 bg-surface-subtle/30 grid h-32 w-full place-items-center rounded-xl border border-dashed">
                    <span className="text-ink-muted text-[12px] font-medium">
                      No image uploaded
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
              <button
                onClick={() => void handleSave()}
                disabled={upsert.isPending}
                className="bg-ink hover:bg-ink/90 flex h-9 items-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
              >
                {upsert.isPending ? (
                  <Spinner size="sm" className="text-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}{' '}
                Save Settings
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 4 — Header Settings
// ═══════════════════════════════════════════════════════════════════════════════

interface HeaderLink {
  label: string;
  url: string;
}

export function HeaderPage() {
  const qc = useQueryClient();

  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'header'],
    queryFn: () => api.cms.getSetting('header'),
  });

  const val = (setting?.value as any) ?? {};
  const [logoUrl, setLogoUrl] = useState('');
  const [stickyHeader, setStickyHeader] = useState(true);
  const [showSearch, setShowSearch] = useState(true);
  const [topBarText, setTopBarText] = useState('');
  const [navLinks, setNavLinks] = useState<HeaderLink[]>([]);
  const [inited, setInited] = useState(false);

  if (setting && !inited) {
    setLogoUrl(val.logoUrl ?? '');
    setStickyHeader(val.stickyHeader ?? true);
    setShowSearch(val.showSearch ?? true);
    setTopBarText(val.topBarText ?? '');
    setNavLinks(
      val.navLinks ?? [
        { label: 'Home', url: '/' },
        { label: 'Shop', url: '/search' },
      ],
    );
    setInited(true);
  }

  const upsert = useMutation({
    mutationFn: () =>
      api.cms.upsertSetting('header', {
        logoUrl,
        stickyHeader,
        showSearch,
        topBarText,
        navLinks,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['website-setting', 'header'] }),
  });

  const logoInputRef = useRef<HTMLInputElement>(null);

  if (isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );

  return (
    <div className="space-y-6 pb-8">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Logo & Top Bar */}
        <div className="space-y-6">
          <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
            <div className="border-border/60 bg-surface-subtle/30 border-b px-5 py-4">
              <h3 className="text-ink flex items-center gap-2 text-[14px] font-bold">
                <ImagePlus className="text-ink-muted h-4 w-4" /> Logo
              </h3>
            </div>
            <div className="space-y-4 p-5">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  className="bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 flex h-9 shrink-0 items-center gap-2 rounded-xl px-4 text-[13px] font-bold transition-all"
                >
                  <ImagePlus className="h-4 w-4" /> Upload
                </button>
                <div className="bg-surface-subtle/50 border-border/60 focus-within:ring-brand-primary/50 flex h-9 flex-1 items-center rounded-xl border px-3 transition-shadow focus-within:ring-1">
                  <input
                    placeholder="Or paste logo URL…"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    className="text-ink w-full bg-transparent text-[13px] font-medium outline-none"
                  />
                </div>
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    try {
                      const sig = await api.files.getUploadSignature({ folder: 'header' });
                      const fd = new FormData();
                      fd.append('file', f);
                      fd.append('api_key', sig.apiKey);
                      fd.append('timestamp', String(sig.timestamp));
                      fd.append('signature', sig.signature);
                      fd.append('folder', sig.folder);
                      const res = await fetch(
                        `https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`,
                        {
                          method: 'POST',
                          body: fd,
                        },
                      );
                      const out = (await res.json()) as { secure_url?: string };
                      if (out.secure_url) setLogoUrl(out.secure_url);
                    } catch {
                      /* ignore */
                    }
                  }
                }}
              />
              {logoUrl && (
                <div className="border-border/60 bg-surface-subtle/20 mt-4 flex items-center justify-center rounded-xl border border-dashed p-4">
                  <img
                    src={logoUrl}
                    alt="Logo preview"
                    className="h-14 w-auto object-contain drop-shadow-sm"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
            <div className="border-border/60 bg-surface-subtle/30 border-b px-5 py-4">
              <h3 className="text-ink text-[14px] font-bold">Top Bar Announcement</h3>
            </div>
            <div className="p-5">
              <label className="text-ink-muted mb-1.5 block text-[12px] font-bold">
                Announcement Text
              </label>
              <div className="bg-surface border-border/60 focus-within:border-ink/50 flex h-10 items-center rounded-xl border px-3 transition-all focus-within:ring-1">
                <input
                  placeholder="Free shipping on orders over ₹999"
                  value={topBarText}
                  onChange={(e) => setTopBarText(e.target.value)}
                  className="text-ink w-full bg-transparent text-[13px] font-medium outline-none"
                />
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
            <div className="border-border/60 bg-surface-subtle/30 border-b px-5 py-4">
              <h3 className="text-ink text-[14px] font-bold">Header Behavior</h3>
            </div>
            <div className="space-y-4 p-5">
              <label className="group flex cursor-pointer items-center justify-between">
                <div>
                  <span className="text-ink group-hover:text-brand-primary text-[13px] font-bold transition-colors">
                    Sticky Header
                  </span>
                  <p className="text-ink-muted text-[11px] font-medium">
                    Keep header visible when scrolling
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setStickyHeader(!stickyHeader)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${stickyHeader ? 'bg-brand-primary' : 'bg-surface-subtle border-border/60 border'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${stickyHeader ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </label>
              <label className="group flex cursor-pointer items-center justify-between">
                <div>
                  <span className="text-ink group-hover:text-brand-primary text-[13px] font-bold transition-colors">
                    Show Search Bar
                  </span>
                  <p className="text-ink-muted text-[11px] font-medium">
                    Display global search in header
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSearch(!showSearch)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${showSearch ? 'bg-brand-primary' : 'bg-surface-subtle border-border/60 border'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${showSearch ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </label>
            </div>
          </div>
        </div>

        {/* Navigation links */}
        <div className="flex flex-col space-y-6">
          <div className="border-border/60 bg-surface flex flex-1 flex-col rounded-2xl border shadow-sm">
            <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
              <h3 className="text-ink text-[14px] font-bold">Navigation Links</h3>
              <button
                type="button"
                onClick={() => setNavLinks([...navLinks, { label: '', url: '' }])}
                className="bg-surface border-border/60 text-ink hover:bg-surface-subtle flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-bold transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add Link
              </button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              {navLinks.map((link, idx) => (
                <div
                  key={idx}
                  className="border-border/40 bg-surface-subtle/20 group relative flex flex-col gap-3 rounded-xl border p-3 sm:flex-row"
                >
                  <div className="flex-1 space-y-1">
                    <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                      Label
                    </label>
                    <input
                      placeholder="e.g. Home"
                      value={link.label}
                      onChange={(e) => {
                        const next = [...navLinks];
                        next[idx] = { ...next[idx]!, label: e.target.value };
                        setNavLinks(next);
                      }}
                      className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                      URL path
                    </label>
                    <input
                      placeholder="e.g. /search"
                      value={link.url}
                      onChange={(e) => {
                        const next = [...navLinks];
                        next[idx] = { ...next[idx]!, url: e.target.value };
                        setNavLinks(next);
                      }}
                      className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setNavLinks(navLinks.filter((_, i) => i !== idx))}
                    className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-red-100 text-red-600 opacity-0 shadow-sm transition-opacity hover:bg-red-200 group-hover:opacity-100"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={3} />
                  </button>
                </div>
              ))}
              {navLinks.length === 0 && (
                <div className="border-border/60 bg-surface flex flex-col items-center justify-center rounded-xl border border-dashed py-12 text-center">
                  <p className="text-ink-muted mb-2 text-[13px] font-medium">
                    No navigation links added yet.
                  </p>
                  <button
                    onClick={() => setNavLinks([{ label: '', url: '' }])}
                    className="text-brand-primary text-[13px] font-bold hover:underline"
                  >
                    Add one now
                  </button>
                </div>
              )}
            </div>
            <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
              <button
                disabled={upsert.isPending}
                onClick={() => upsert.mutate()}
                className="bg-ink hover:bg-ink/90 flex h-9 min-w-[120px] items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
              >
                {upsert.isPending ? (
                  <Spinner size="sm" className="text-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}{' '}
                Save Header Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 5 — Footer Settings
// ═══════════════════════════════════════════════════════════════════════════════

interface FooterLink {
  label: string;
  url: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  platform: string;
  url: string;
}

export function FooterPage() {
  const qc = useQueryClient();

  const { data: setting, isLoading } = useQuery({
    queryKey: ['website-setting', 'footer'],
    queryFn: () => api.cms.getSetting('footer'),
  });

  const val = (setting?.value as any) ?? {};
  const [copyrightText, setCopyrightText] = useState('');
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [columns, setColumns] = useState<FooterColumn[]>([]);
  const [inited, setInited] = useState(false);

  if (setting && !inited) {
    setCopyrightText(
      val.copyrightText ??
        `© ${new Date().getFullYear()} Terra Shoppy. All rights reserved. Developed by BDM Cloudtech Pvt. Ltd.`,
    );
    setSocialLinks(
      val.socialLinks ?? [
        { platform: 'Facebook', url: '' },
        { platform: 'Instagram', url: '' },
        { platform: 'Twitter', url: '' },
      ],
    );
    setColumns(
      val.columns ?? [
        {
          title: 'Quick Links',
          links: [
            { label: 'About Us', url: '/page/about' },
            { label: 'Contact', url: '/page/contact' },
          ],
        },
        {
          title: 'Customer Service',
          links: [
            { label: 'FAQ', url: '/page/faq' },
            { label: 'Returns', url: '/page/returns' },
          ],
        },
      ],
    );
    setInited(true);
  }

  const upsert = useMutation({
    mutationFn: () =>
      api.cms.upsertSetting('footer', {
        copyrightText,
        socialLinks,
        columns,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['website-setting', 'footer'] }),
  });

  if (isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );

  return (
    <div className="space-y-6 pb-8">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Side: Copyright & Social */}
        <div className="flex flex-col space-y-6">
          <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
            <div className="border-border/60 bg-surface-subtle/30 border-b px-5 py-4">
              <h3 className="text-ink text-[14px] font-bold">Copyright Text</h3>
            </div>
            <div className="p-5">
              <div className="bg-surface border-border/60 focus-within:border-ink/50 flex h-10 items-center rounded-xl border px-3 transition-all focus-within:ring-1">
                <input
                  placeholder="© 2026 Terra Shoppy…"
                  value={copyrightText}
                  onChange={(e) => setCopyrightText(e.target.value)}
                  className="text-ink w-full bg-transparent text-[13px] font-medium outline-none"
                />
              </div>
            </div>
          </div>

          <div className="border-border/60 bg-surface flex flex-1 flex-col rounded-2xl border shadow-sm">
            <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
              <h3 className="text-ink text-[14px] font-bold">Social Links</h3>
              <button
                type="button"
                onClick={() => setSocialLinks([...socialLinks, { platform: '', url: '' }])}
                className="bg-surface border-border/60 text-ink hover:bg-surface-subtle flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-bold transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add Social
              </button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              {socialLinks.map((link, idx) => (
                <div
                  key={idx}
                  className="border-border/40 bg-surface-subtle/20 group relative flex flex-col gap-3 rounded-xl border p-3 sm:flex-row"
                >
                  <div className="w-full space-y-1 sm:w-1/3">
                    <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                      Platform
                    </label>
                    <input
                      placeholder="e.g. Facebook"
                      value={link.platform}
                      onChange={(e) => {
                        const next = [...socialLinks];
                        next[idx] = { ...next[idx]!, platform: e.target.value };
                        setSocialLinks(next);
                      }}
                      className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                      URL
                    </label>
                    <input
                      placeholder="https://…"
                      value={link.url}
                      onChange={(e) => {
                        const next = [...socialLinks];
                        next[idx] = { ...next[idx]!, url: e.target.value };
                        setSocialLinks(next);
                      }}
                      className="bg-surface border-border/60 focus:border-brand-primary/50 h-9 w-full rounded-lg border px-3 text-[13px] font-medium outline-none transition-colors"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setSocialLinks(socialLinks.filter((_, i) => i !== idx))}
                    className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-red-100 text-red-600 opacity-0 shadow-sm transition-opacity hover:bg-red-200 group-hover:opacity-100"
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={3} />
                  </button>
                </div>
              ))}
              {socialLinks.length === 0 && (
                <div className="border-border/60 bg-surface flex flex-col items-center justify-center rounded-xl border border-dashed py-8 text-center">
                  <p className="text-ink-muted text-[13px] font-medium">No social links.</p>
                </div>
              )}
            </div>
            <div className="border-border/60 bg-surface-subtle/30 flex justify-end border-t px-5 py-4">
              <button
                disabled={upsert.isPending}
                onClick={() => upsert.mutate()}
                className="bg-ink hover:bg-ink/90 flex h-9 min-w-[120px] items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
              >
                {upsert.isPending ? (
                  <Spinner size="sm" className="text-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}{' '}
                Save Settings
              </button>
            </div>
          </div>
        </div>

        {/* Right Side: Columns */}
        <div className="flex flex-col space-y-6">
          <div className="border-border/60 bg-surface flex flex-1 flex-col rounded-2xl border shadow-sm">
            <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
              <h3 className="text-ink text-[14px] font-bold">Footer Columns (Link Lists)</h3>
              <button
                type="button"
                onClick={() =>
                  setColumns([...columns, { title: '', links: [{ label: '', url: '' }] }])
                }
                className="bg-surface border-border/60 text-ink hover:bg-surface-subtle flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-bold transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add Column
              </button>
            </div>
            <div className="flex-1 space-y-5 overflow-y-auto p-5">
              {columns.map((col, colIdx) => (
                <div
                  key={colIdx}
                  className="border-border/60 group/col bg-surface-subtle/10 relative rounded-xl border p-4 shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => setColumns(columns.filter((_, i) => i !== colIdx))}
                    className="absolute -right-2 -top-2 z-10 grid h-6 w-6 place-items-center rounded-full bg-red-100 text-red-600 opacity-0 shadow-sm transition-opacity hover:bg-red-200 group-hover/col:opacity-100"
                  >
                    <Trash2 className="h-3 w-3" strokeWidth={2.5} />
                  </button>
                  <div className="mb-4">
                    <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                      Column Title
                    </label>
                    <input
                      placeholder="e.g. Quick Links"
                      value={col.title}
                      onChange={(e) => {
                        const next = [...columns];
                        next[colIdx] = { ...next[colIdx]!, title: e.target.value };
                        setColumns(next);
                      }}
                      className="bg-surface border-border/60 text-ink focus:border-brand-primary/50 mt-1 h-10 w-full rounded-lg border px-3 text-[14px] font-bold shadow-sm outline-none transition-colors"
                    />
                  </div>
                  <div className="border-border/40 space-y-2 border-t pt-3">
                    {col.links.map((link, linkIdx) => (
                      <div
                        key={linkIdx}
                        className="group/link bg-surface border-border/40 relative flex items-center gap-2 rounded-lg border p-1.5"
                      >
                        <input
                          placeholder="Link Label"
                          value={link.label}
                          onChange={(e) => {
                            const next = [...columns];
                            const links = [...next[colIdx]!.links];
                            links[linkIdx] = { ...links[linkIdx]!, label: e.target.value };
                            next[colIdx] = { ...next[colIdx]!, links };
                            setColumns(next);
                          }}
                          className="placeholder:text-ink-muted/50 h-8 flex-1 bg-transparent px-2 text-[12px] font-medium outline-none"
                        />
                        <div className="bg-border/60 h-5 w-px shrink-0" />
                        <input
                          placeholder="Link URL"
                          value={link.url}
                          onChange={(e) => {
                            const next = [...columns];
                            const links = [...next[colIdx]!.links];
                            links[linkIdx] = { ...links[linkIdx]!, url: e.target.value };
                            next[colIdx] = { ...next[colIdx]!, links };
                            setColumns(next);
                          }}
                          className="placeholder:text-ink-muted/50 h-8 flex-1 bg-transparent px-2 text-[12px] font-medium outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const next = [...columns];
                            next[colIdx] = {
                              ...next[colIdx]!,
                              links: next[colIdx]!.links.filter((_, i) => i !== linkIdx),
                            };
                            setColumns(next);
                          }}
                          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-red-400 opacity-0 transition-colors hover:bg-red-50 hover:text-red-600 group-hover/link:opacity-100"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="text-brand-primary mt-2 flex items-center gap-1 px-1 text-[12px] font-bold hover:underline"
                      onClick={() => {
                        const next = [...columns];
                        next[colIdx] = {
                          ...next[colIdx]!,
                          links: [...next[colIdx]!.links, { label: '', url: '' }],
                        };
                        setColumns(next);
                      }}
                    >
                      <Plus className="h-3 w-3" /> Add Link to Column
                    </button>
                  </div>
                </div>
              ))}
              {columns.length === 0 && (
                <div className="border-border/60 bg-surface flex flex-col items-center justify-center rounded-xl border border-dashed py-12 text-center">
                  <p className="text-ink-muted mb-2 text-[13px] font-medium">No footer columns.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE 6 — Pages (CMS Static Pages)
// ═══════════════════════════════════════════════════════════════════════════════

export function PagesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<CmsPageType | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ slug: '', title: '', contentHtml: '', published: false });
  const [query, setQuery] = useState('');

  const { data: pages, isLoading } = useQuery({
    queryKey: ['cms-pages-admin'],
    queryFn: () => api.cms.listPages(),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['cms-pages-admin'] });

  const createMutation = useMutation({
    mutationFn: () => api.cms.createPage(form),
    onSuccess: () => {
      invalidate();
      setCreating(false);
      setForm({ slug: '', title: '', contentHtml: '', published: false });
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => api.cms.updatePage(editing!.id, form),
    onSuccess: () => {
      invalidate();
      setEditing(null);
      setForm({ slug: '', title: '', contentHtml: '', published: false });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.cms.deletePage(id),
    onSuccess: invalidate,
  });

  function startEdit(page: CmsPageType) {
    setEditing(page);
    setForm({
      slug: page.slug,
      title: page.title,
      contentHtml: page.contentHtml,
      published: page.published,
    });
    setCreating(false);
  }

  function startCreate() {
    setCreating(true);
    setEditing(null);
    setForm({ slug: '', title: '', contentHtml: '', published: false });
  }

  const showForm = creating || editing;

  const displayPages = useMemo(() => {
    if (!pages) return [];
    return pages.filter(
      (p: CmsPageType) =>
        p.title.toLowerCase().includes(query.toLowerCase()) ||
        p.slug.toLowerCase().includes(query.toLowerCase()),
    );
  }, [pages, query]);

  return (
    <div className="space-y-6 pb-8">
      {showForm ? (
        <div className="border-border/60 bg-surface rounded-2xl border shadow-sm">
          <div className="border-border/60 bg-surface-subtle/30 border-b px-5 py-4">
            <h3 className="text-ink flex items-center gap-2 text-[14px] font-bold">
              <FileText className="text-ink-muted h-4 w-4" />
              {editing ? 'Edit Page' : 'Create Page'}
            </h3>
          </div>
          <div className="space-y-5 p-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                  Title
                </label>
                <input
                  placeholder="About Us"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="bg-surface border-border/60 text-ink focus:border-brand-primary/50 h-10 w-full rounded-lg border px-3 text-[13px] font-bold shadow-sm outline-none transition-colors"
                />
              </div>
              <div className="space-y-1">
                <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                  Slug
                </label>
                <input
                  placeholder="about-us"
                  value={form.slug}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      slug: e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, '-')
                        .replace(/-+/g, '-'),
                    }))
                  }
                  className="bg-surface-subtle/50 border-border/60 text-ink focus:border-brand-primary/50 h-10 w-full rounded-lg border px-3 font-mono text-[13px] font-medium shadow-sm outline-none transition-colors"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-ink-muted ml-1 text-[10px] font-bold uppercase tracking-wider">
                Content (HTML)
              </label>
              <textarea
                rows={12}
                value={form.contentHtml}
                onChange={(e) => setForm((f) => ({ ...f, contentHtml: e.target.value }))}
                className="bg-surface border-border/60 text-ink focus:border-brand-primary/50 w-full resize-y rounded-xl border p-4 font-mono text-[13px] leading-relaxed shadow-sm outline-none transition-colors"
                placeholder="<h2>Welcome</h2><p>…</p>"
              />
            </div>

            <label className="group flex cursor-pointer items-center justify-between">
              <div>
                <span className="text-ink group-hover:text-brand-primary text-[13px] font-bold transition-colors">
                  Publish Page
                </span>
                <p className="text-ink-muted text-[11px] font-medium">
                  Make this page visible to customers
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, published: !f.published }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.published ? 'bg-brand-primary' : 'bg-surface-subtle border-border/60 border'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${form.published ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
            </label>
          </div>

          <div className="border-border/60 bg-surface-subtle/30 flex justify-end gap-3 border-t px-5 py-4">
            <button
              onClick={() => {
                setCreating(false);
                setEditing(null);
              }}
              className="bg-surface border-border/60 text-ink hover:bg-surface-subtle h-9 rounded-xl border px-4 text-[13px] font-bold transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={
                !form.title.trim() ||
                !form.slug.trim() ||
                (editing ? updateMutation.isPending : createMutation.isPending)
              }
              onClick={() => (editing ? updateMutation.mutate() : createMutation.mutate())}
              className="bg-ink hover:bg-ink/90 flex h-9 items-center justify-center gap-2 rounded-xl px-5 text-[13px] font-bold text-white transition-all hover:shadow-sm disabled:opacity-50"
            >
              {(editing ? updateMutation.isPending : createMutation.isPending) ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {editing ? 'Update Page' : 'Create Page'}
            </button>
          </div>
        </div>
      ) : (
        <div className="border-border/60 bg-surface flex flex-col overflow-hidden rounded-2xl border shadow-sm">
          <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between gap-4 border-b p-4">
            <div className="bg-surface border-border/60 focus-within:ring-brand-primary/50 flex h-10 w-full max-w-sm items-center rounded-xl border px-3 transition-shadow focus-within:ring-1">
              <Search className="text-ink-muted mr-2 h-4 w-4" />
              <input
                placeholder="Search pages by title or slug..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="text-ink placeholder:text-ink-muted/50 w-full bg-transparent text-[13px] font-medium outline-none"
              />
            </div>
            <button
              type="button"
              onClick={startCreate}
              className="bg-ink hover:bg-ink/90 flex h-10 shrink-0 items-center gap-2 rounded-xl px-4 text-[13px] font-bold text-white transition-all hover:shadow-sm"
            >
              <Plus className="h-4 w-4" /> Add Page
            </button>
          </div>

          {isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : displayPages.length === 0 ? (
            <div className="grid min-h-[300px] place-items-center p-6 text-center">
              <div>
                <FileText className="text-ink-muted/30 mx-auto mb-3 h-12 w-12" strokeWidth={1.5} />
                <p className="text-ink text-[14px] font-bold">No pages found</p>
                <p className="text-ink-muted mt-1 max-w-sm text-[13px] font-medium">
                  You haven&apos;t created any custom pages yet, or no pages match your search.
                </p>
                <button
                  type="button"
                  onClick={startCreate}
                  className="bg-surface border-border/60 text-ink hover:bg-surface-subtle mt-4 h-9 rounded-xl border px-4 text-[13px] font-bold transition-colors"
                >
                  Create your first page
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[13px]">
                <thead className="bg-surface-subtle/50 text-ink-muted text-[11px] font-bold uppercase tracking-wider">
                  <tr>
                    <th className="border-border/60 border-b px-5 py-3 font-bold">Page Title</th>
                    <th className="border-border/60 border-b px-5 py-3 font-bold">Slug</th>
                    <th className="border-border/60 border-b px-5 py-3 font-bold">Status</th>
                    <th className="border-border/60 border-b px-5 py-3 text-right font-bold">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y">
                  {displayPages.map((page: CmsPageType) => (
                    <tr
                      key={page.id}
                      className="hover:bg-surface-subtle/20 group transition-colors"
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="bg-surface-subtle border-border/60 grid h-8 w-8 place-items-center rounded-lg border">
                            <FileText className="text-ink-muted h-4 w-4" />
                          </div>
                          <span className="text-ink font-bold">{page.title}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <code className="text-ink-muted bg-surface-subtle/50 border-border/40 rounded border px-1.5 py-0.5 font-mono text-[12px]">
                          /{page.slug}
                        </code>
                      </td>
                      <td className="px-5 py-3.5">
                        {page.published ? (
                          <span className="inline-flex items-center gap-1.5 rounded-md bg-green-50 px-2 py-1 text-[11px] font-bold text-green-700 ring-1 ring-inset ring-green-600/20">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Published
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-md bg-yellow-50 px-2 py-1 text-[11px] font-bold text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
                            <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" /> Draft
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            onClick={() => startEdit(page)}
                            className="text-ink-muted hover:bg-surface-subtle hover:text-ink grid h-8 w-8 place-items-center rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete "${page.title}"?`))
                                deleteMutation.mutate(page.id);
                            }}
                            disabled={deleteMutation.isPending}
                            className="text-ink-muted grid h-8 w-8 place-items-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
